import React, { useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import { Button, Checkbox, Typography } from "@mui/material";

//Icons
import { ChevronDown } from "lucide-react";
import { ReactComponent as HelpIcon } from "../../../../assets/icons/svg/question-circle.svg";

//Modals
import CustomModal from "../../../modal/CustomModal";
import RemoveItemHelp from "../help/RemoveItemHelp";

//Actions
import { handleSingleOnHandRemove } from "../../../../actions/pantry";

export const RemoveItemModal = ({ modal: { pantryItemID, locationID, loading, error }, handleSingleOnHandRemove }) => {
  const [removeType, setRemoveType] = useState(null);
  const [showHelpModal, setShowHelpModal] = useState(false);

  const handleConsumedClick = () => {
    setRemoveType("consumed");
  };

  const handleWastedClick = () => {
    setRemoveType("wasted");
  };

  const handleVoidClick = () => {
    setRemoveType("void");
  };

  const handleExpiredClick = () => {
    setRemoveType("expired");
  };

  return (
    <div className="w-full">
      <CustomModal open={showHelpModal} handleClose={() => setShowHelpModal(false)} type="bottom">
        <RemoveItemHelp />
      </CustomModal>
      <div className="p-4 bg-neutral-100 flex flex-row justify-between items-center">
        <Typography variant="h6" className="font-normal">
          Remove Item
        </Typography>
        <Button
          size="small"
          disableRipple
          className="hover:bg-transparent border-neutral-400 hover:border-neutral-500"
          variant="outlined"
          onClick={() => setShowHelpModal(true)}
        >
          <HelpIcon className="w-4 h-4 mr-1" />
          Help
        </Button>
      </div>
      <div>
        <div className="flex flex-col gap-2 py-4">
          <Button fullWidth disableRipple className="text-start" onClick={handleConsumedClick}>
            <div className="flex flex-row items-start gap-2 w-full">
              <Checkbox checked={removeType === "consumed"} onChange={handleConsumedClick} />
              <div className="w-full items-start">
                <Typography variant="subtitle1" className="font-medium">
                  Consumed
                </Typography>
                <Typography variant="body2" className="text-secondaryText-600">
                  I used or ate all of this ingredient
                </Typography>
              </div>
            </div>
          </Button>
          <Button fullWidth disableRipple className="text-start" onClick={handleWastedClick}>
            <div className="flex flex-row items-start gap-2 w-full">
              <Checkbox checked={removeType === "wasted"} onChange={handleWastedClick} />
              <div className="w-full items-start">
                <Typography variant="subtitle1" className="font-medium">
                  Wasted
                </Typography>
                <Typography variant="body2" className="text-secondaryText-600">
                  I threw this ingredient away
                </Typography>
              </div>
            </div>
          </Button>
          <Button fullWidth disableRipple className="text-start" onClick={handleExpiredClick}>
            <div className="flex flex-row items-start gap-2 w-full">
              <Checkbox checked={removeType === "expired"} onChange={handleExpiredClick} />
              <div className="w-full items-start">
                <Typography variant="subtitle1" className="font-medium">
                  Expired
                </Typography>
                <Typography variant="body2" className="text-secondaryText-600">
                  This ingredient expired before I could use it
                </Typography>
              </div>
            </div>
          </Button>
          <Button fullWidth disableRipple className="text-start" onClick={handleVoidClick}>
            <div className="flex flex-row items-start gap-2 w-full">
              <Checkbox checked={removeType === "void"} onChange={handleVoidClick} />
              <div className="w-full items-start">
                <Typography variant="subtitle1" className="font-medium">
                  Void
                </Typography>
                <Typography variant="body2" className="text-secondaryText-600">
                  This ingredient was accidentally added or is invalid
                </Typography>
              </div>
            </div>
          </Button>
        </div>
        <div className="w-full mt-2 px-4 pb-4">
          <Button
            variant="contained"
            color="primary"
            disabled={!removeType || loading}
            onClick={() => handleSingleOnHandRemove(pantryItemID, locationID, removeType)}
            disableRipple
            disableElevation
            fullWidth
          >
            Remove Item
          </Button>
        </div>
      </div>
    </div>
  );
};

RemoveItemModal.propTypes = {
  pantryItemID: PropTypes.string.isRequired,
  handleSingleOnHandRemove: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  modal: state?.pantry?.modals?.removeItem,
});

export default connect(mapStateToProps, { handleSingleOnHandRemove })(RemoveItemModal);
